import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Car, Gem, Sparkles, Loader2, Plus, Minus, Edit3, Flag,
  Wrench, Store, Trophy, Gauge,
} from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { themeOf, ARENA, CAR_SLOT_THEME, rarityOf } from '../lib/classTheme';
import {
  ArenaFloor, ArenaTitle, ArenaTabs, ArenaStatBar, Pedestal,
  Laurel, ArenaEmpty, RarityBadge, ProgressRing,
} from '../components/ArenaFrame';
import { Sheet, SkeletonList } from '../components/ui';

const CAR_STAT_COLORS = {
  speed: '#c0392b', acceleration: '#d35400', handling: '#2980b9',
  nitro: '#8e44ad', durability: '#7a6749', grip: '#16a085',
};

export default function Garage() {
  const qc = useQueryClient();
  const { user, load } = useAuth();
  const [tab, setTab] = useState('stats');
  const [alloc, setAlloc] = useState({});
  const [buyAlloc, setBuyAlloc] = useState({});
  const [buyOpen, setBuyOpen] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['car'], queryFn: () => api.get('/cars').then(r => r.data) });
  const { data: meta } = useQuery({ queryKey: ['carMeta'], queryFn: () => api.get('/cars/meta').then(r => r.data) });
  const { data: parts } = useQuery({
    queryKey: ['carParts'], queryFn: () => api.get('/cars/parts').then(r => r.data),
    enabled: tab === 'tuning',
  });

  const refresh = () => { qc.invalidateQueries(['car', 'carParts']); load(); };

  const allocM = useMutation({
    mutationFn: () => api.post('/cars/allocate', { allocation: alloc }),
    onSuccess: () => { toast.success('Tuned! 🔧'); setAlloc({}); refresh(); },
    onError: e => toast.error(e.message),
  });

  const buyM = useMutation({
    mutationFn: () => api.post('/cars/buy-stats', { allocation: buyAlloc }),
    onSuccess: ({ data: d }) => { toast.success(`+${d.statsAdded} · −💎${d.spent}`); setBuyAlloc({}); refresh(); },
    onError: e => toast.error(e.message),
  });

  const installM = useMutation({
    mutationFn: (id) => api.post(`/cars/install/${id}`),
    onSuccess: () => { toast.success('Installed!'); refresh(); },
    onError: e => toast.error(e.message),
  });

  const removeM = useMutation({
    mutationFn: (slot) => api.post(`/cars/remove/${slot}`),
    onSuccess: () => { toast('Removed'); refresh(); },
  });

  const renameM = useMutation({
    mutationFn: (name) => api.post('/cars/rename', { name }),
    onSuccess: () => { toast.success('Renamed!'); refresh(); },
  });

  const totalAlloc = Object.values(alloc).reduce((a, b) => a + (b || 0), 0);
  const totalBuy = Object.values(buyAlloc).reduce((a, b) => a + (b || 0), 0);
  const buyCost = totalBuy * (meta?.statCost || 3);

  if (isLoading || !meta) return <SkeletonList n={4} h="h-40" />;

  const car = data?.car;
  const t = themeOf(data?.classKey);

  if (!car) {
    return (
      <div className="space-y-4 animate-fade-up">
        <div className="card-arena arena-arches">
          <ArenaFloor classKey={data?.classKey} className="p-6 text-center" showCrowd={false}>
            <motion.div animate={{ x: [-6, 6, -6] }} transition={{ duration: 2.4, repeat: Infinity }}>
              <Car size={48} className="mx-auto mb-3" style={{ color: ARENA.goldL }} />
            </motion.div>
            <h1 className="font-display text-2xl font-extrabold tracking-wide"
              style={{ color: ARENA.goldL }}>Garage</h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(232,220,200,.7)' }}>
              Claim your chariot and race in the Circus Maximus
            </p>
            <button onClick={() => setBuyOpen(true)} className="btn-primary mt-4">
              <Sparkles size={15} /> Choose your chariot
            </button>
            <p className="text-[11px] mt-2 font-semibold" style={{ color: 'rgba(232,220,200,.5)' }}>
              First one is <b style={{ color: ARENA.goldL }}>free</b>
            </p>
          </ArenaFloor>
        </div>
        <BuyCarSheet open={buyOpen} onClose={() => setBuyOpen(false)}
          classKey={data?.classKey} onSuccess={refresh} />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena arena-arches overflow-hidden">
        <ArenaFloor classKey={data.classKey} className="p-5" showCrowd={false}>
          <Pedestal glow={t.hex} width="70%">
            <motion.div className="rounded-xl overflow-hidden mx-auto max-w-[220px]"
              style={{ padding: 2.5, background: ARENA.goldFrame, boxShadow: t.glowCss }}
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}>
              <div className="rounded-[10px] overflow-hidden bg-stone-900" style={{ aspectRatio: '9/13' }}>
                <img src={car.image} alt={car.name} className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </Pedestal>

          <div className="text-center mt-4 text-white">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Laurel size={20} color={ARENA.goldL} />
              <span className="text-[9px] font-display font-black uppercase tracking-[0.2em]"
                style={{ color: ARENA.goldL }}>{car.modelType}</span>
            </div>
            <button onClick={() => { const n = prompt('Chariot name', car.name); if (n) renameM.mutate(n); }}
              className="inline-flex items-center gap-1.5 group">
              <h1 className="font-display text-xl font-extrabold tracking-wide"
                style={{ color: ARENA.goldL, textShadow: '0 2px 14px rgba(201,151,58,.45)' }}>
                {car.name}
              </h1>
              <Edit3 size={12} className="opacity-0 group-hover:opacity-60 transition"
                style={{ color: ARENA.goldL }} />
            </button>

            <div className="mt-3 max-w-xs mx-auto">
              <div className="flex justify-between text-[10px] font-display font-bold mb-1"
                style={{ color: ARENA.goldL }}>
                <span>LVL {car.level}</span>
                <span>XP {compact(car.xpProgress.current)}/{compact(car.xpProgress.needed)}</span>
              </div>
              <ArenaStatBar value={car.xpProgress.current} max={car.xpProgress.needed} height={6} />
            </div>

            <div className="grid grid-cols-4 gap-1.5 mt-3 max-w-sm mx-auto">
              <Mini l="POWER"  v={car.power} />
              <Mini l="TUNING" v={car.tuningScore} />
              <Mini l="ELO"    v={car.raceElo} />
              <Mini l="WINS"   v={`${car.wins}/${car.races}`} />
            </div>
          </div>
        </ArenaFloor>

        <div className="p-3 grid grid-cols-3 gap-2" style={{ background: 'var(--sand-100)' }}>
          <Link to="/race" className="btn-primary btn-sm flex-col !py-2.5 h-auto">
            <Flag size={15} /><span className="text-[10px] font-display">Race</span>
          </Link>
          <Link to="/parts-shop" className="btn-ghost btn-sm flex-col !py-2.5 h-auto">
            <Store size={15} /><span className="text-[10px] font-display">Parts</span>
          </Link>
          <button onClick={() => setBuyOpen(true)} className="btn-ghost btn-sm flex-col !py-2.5 h-auto">
            <Car size={15} /><span className="text-[10px] font-display">Garage</span>
          </button>
        </div>
      </div>

      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'stats',  label: 'Performance', icon: '⚡' },
        { key: 'tuning', label: 'Tuning', icon: '🔧' },
      ]} />

      {tab === 'stats' && (
        <>
          {car.statPoints > 0 && (
            <div className="card p-4" style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wrench size={17} style={{ color: ARENA.gold }} />
                  <span className="font-display font-extrabold text-sm">
                    {car.statPoints - totalAlloc} free tuning points
                  </span>
                </div>
                {totalAlloc > 0 && (
                  <button onClick={() => allocM.mutate()} className="btn-primary btn-sm">
                    Apply {totalAlloc}</button>
                )}
              </div>
            </div>
          )}

          <div className="card p-4" style={{ borderColor: '#34d399', borderWidth: 2 }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gem size={17} className="text-mint-600" />
                <span className="font-display font-extrabold text-sm">
                  {meta.statCost} points = 1 tuning
                </span>
              </div>
              {totalBuy > 0 && (
                <button onClick={() => buyM.mutate()}
                  disabled={buyCost > user.points_balance}
                  className="btn-mint btn-sm">Buy {totalBuy} · 💎{compact(buyCost)}</button>
              )}
            </div>
          </div>

          <div className="card p-4 space-y-2.5">
            {meta.statKeys.map(k => {
              const m = meta.stats[k];
              const base = car.baseStats[k];
              const eq = car.tuningStats[k] || 0;
              const pending = (alloc[k] || 0) + (buyAlloc[k] || 0);
              const total = base + eq + pending;

              return (
                <div key={k} className="flex items-center gap-3">
                  <span className="text-base w-6 text-center shrink-0">{m.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm font-display font-bold">{m.label}</span>
                      <span className="text-sm font-display font-black tabular-nums">
                        {base}
                        {eq > 0 && <span className="text-mint-600 text-xs"> +{eq}</span>}
                        {pending > 0 && <span className="text-xs" style={{ color: ARENA.goldD }}> +{pending}</span>}
                      </span>
                    </div>
                    <div className="mt-1">
                      <ArenaStatBar value={total} max={100} height={7}
                        color={`linear-gradient(90deg,${CAR_STAT_COLORS[k]},${CAR_STAT_COLORS[k]}cc)`} />
                    </div>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--stone-400)' }}>{m.desc}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {car.statPoints - totalAlloc > 0 && (
                      <button onClick={() => setAlloc({ ...alloc, [k]: (alloc[k] || 0) + 1 })}
                        className="w-7 h-7 rounded-lg grid place-items-center text-white active:scale-90"
                        style={{ background: ARENA.goldFrame }}><Plus size={13} strokeWidth={3} /></button>
                    )}
                    {(alloc[k] > 0 || buyAlloc[k] > 0) && (
                      <button onClick={() => {
                        if (alloc[k] > 0) setAlloc({ ...alloc, [k]: alloc[k] - 1 });
                        else setBuyAlloc({ ...buyAlloc, [k]: buyAlloc[k] - 1 });
                      }} className="w-7 h-7 rounded-lg grid place-items-center"
                        style={{ background: 'var(--stone-200)', color: 'var(--stone-600)' }}>
                        <Minus size={13} strokeWidth={3} /></button>
                    )}
                    <button onClick={() => setBuyAlloc({ ...buyAlloc, [k]: (buyAlloc[k] || 0) + 1 })}
                      className="w-7 h-7 rounded-lg grid place-items-center text-white bg-mint-500 active:scale-90">
                      <Gem size={11} /></button>
                  </div>
                </div>
              );
            })}
          </div>

          {car.races > 0 && (
            <div className="card p-3.5">
              <ArenaTitle title="Race record" latin="Acta Cursus" />
              <div className="grid grid-cols-4 gap-2 text-center">
                <RecordStat l="Races"   v={car.races} />
                <RecordStat l="Wins"    v={car.wins} color={ARENA.gold} />
                <RecordStat l="Winrate" v={`${car.winrate}%`} />
                <RecordStat l="Best"    v={car.bestTimeMs ? `${(car.bestTimeMs / 1000).toFixed(1)}s` : '—'} />
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'tuning' && (
        <>
          <div className="card overflow-hidden">
            <ArenaFloor classKey={data.classKey} className="p-4" showCrowd={false}>
              <div className="grid grid-cols-4 gap-2">
                {Object.keys(CAR_SLOT_THEME).map(s => (
                  <PartSlot key={s} slot={s} item={car.parts?.[s]}
                    onRemove={() => removeM.mutate(s)} />
                ))}
              </div>
              <p className="text-center text-[10px] font-display font-bold mt-3"
                style={{ color: ARENA.goldL }}>
                {car.tuningScore} TUNING SCORE · {car.partsCount}/4 INSTALLED
              </p>
            </ArenaFloor>
          </div>

          <ArenaTitle title="Parts inventory" latin="Partes"
            right={<Link to="/parts-shop" className="btn-primary btn-sm"><Store size={12} /> Shop</Link>} />

          {!parts?.length
            ? <ArenaEmpty emoji="⚙️" title="No parts yet"
                sub="Install parts to boost your chariot's performance."
                action={<Link to="/parts-shop" className="btn-primary btn-sm">Visit parts shop</Link>} />
            : <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                {parts.map(p => (
                  <PartCard key={p.id} part={p}
                    onClick={() => !p.equipped && installM.mutate(p.id)} />
                ))}
              </div>}
        </>
      )}

      <BuyCarSheet open={buyOpen} onClose={() => setBuyOpen(false)}
        classKey={data.classKey} owned={data.cars} onSuccess={refresh} />
    </div>
  );
}

/* ---------------- SUB ---------------- */
function Mini({ l, v }) {
  return (
    <div className="rounded-lg px-2 py-1.5 text-center"
      style={{ background: 'rgba(232,200,116,.14)', border: '1px solid rgba(232,200,116,.22)' }}>
      <p className="text-[8px] font-display font-bold tracking-wider"
        style={{ color: 'rgba(232,220,200,.6)' }}>{l}</p>
      <p className="font-display font-black text-sm" style={{ color: ARENA.goldL }}>{v}</p>
    </div>
  );
}

function RecordStat({ l, v, color }) {
  return (
    <div className="rounded-xl p-2" style={{ background: 'var(--sand-100)' }}>
      <p className="font-display font-black text-base" style={{ color: color || 'var(--stone-800)' }}>{v}</p>
      <p className="text-[9px] font-display font-bold" style={{ color: 'var(--stone-400)' }}>{l}</p>
    </div>
  );
}

function PartSlot({ slot, item, onRemove }) {
  const s = CAR_SLOT_THEME[slot];
  const r = item ? rarityOf(item.rarity) : null;
  return (
    <div className="relative">
      <div className="w-full aspect-square rounded-xl overflow-hidden"
        style={item
          ? { padding: 2, background: `linear-gradient(135deg,${r.hex},${r.glow})`, boxShadow: r.shadowCss }
          : { border: '2px dashed rgba(232,200,116,.3)' }}>
        {item ? (
          <div className="w-full h-full rounded-lg overflow-hidden bg-stone-900">
            <img src={item.thumb || item.image} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-full h-full grid place-items-center text-lg opacity-30">{s.icon}</div>
        )}
      </div>
      {item && onRemove && (
        <button onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-[10px]
            grid place-items-center shadow-lg" style={{ background: ARENA.blood }}>✕</button>
      )}
      <p className="text-[7px] font-display font-black text-center mt-0.5 tracking-wider"
        style={{ color: 'rgba(232,220,200,.55)' }}>{s.latin}</p>
    </div>
  );
}

function PartCard({ part, onClick }) {
  const r = rarityOf(part.rarity);
  const s = CAR_SLOT_THEME[part.slot];
  return (
    <motion.button onClick={onClick} whileTap={{ scale: .95 }}
      className="relative rounded-xl overflow-hidden text-left"
      style={{ padding: 2, background: `linear-gradient(135deg,${r.hex}55,${r.glow}55)` }}>
      <div className="rounded-[10px] overflow-hidden" style={{ background: 'var(--marble)' }}>
        <div className="relative aspect-square" style={{ background: 'var(--stone-100)' }}>
          {part.thumb || part.image
            ? <img src={part.thumb || part.image} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full grid place-items-center text-2xl opacity-30">{s.icon}</div>}
          {part.equipped && <span className="absolute top-1 left-1 chip-mint !text-[7px]">INSTALLED</span>}
        </div>
        <div className="p-1.5">
          <RarityBadge rarity={part.rarity} />
          <p className="font-bold text-[10px] leading-tight line-clamp-2 mt-0.5">{part.name}</p>
          <p className="text-[9px] font-black mt-0.5" style={{ color: r.hex }}>+{part.totalStats}</p>
        </div>
      </div>
    </motion.button>
  );
}

function BuyCarSheet({ open, onClose, classKey, owned = [], onSuccess }) {
  const [sel, setSel] = useState(null);
  const [name, setName] = useState('');

  const { data } = useQuery({
    queryKey: ['carTemplates', classKey],
    queryFn: () => api.get(`/cars/templates/${classKey}`).then(r => r.data),
    enabled: open && !!classKey,
  });

  const buy = useMutation({
    mutationFn: () => api.post('/cars/buy', { templateId: sel, name: name || undefined }),
    onSuccess: ({ data: d }) => {
      toast.success(d.free ? `${d.name} is yours — free! 🏎️` : `${d.name} acquired · −💎${d.price}`);
      onClose(); onSuccess();
    },
    onError: e => toast.error(e.message),
  });

  const switchM = useMutation({
    mutationFn: (id) => api.post(`/cars/switch/${id}`),
    onSuccess: () => { toast.success('Chariot ready'); onClose(); onSuccess(); },
  });

  const t = themeOf(classKey);
  const ownedIds = new Set(owned.map(c => c.templateId));

  return (
    <Sheet open={open} onClose={onClose} title="Choose your chariot" wide>
      {owned.length > 0 && (
        <div className="mb-4">
          <p className="label">Your garage</p>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {owned.map(c => (
              <button key={c.id} onClick={() => !c.active && switchM.mutate(c.id)}
                className="shrink-0 w-[84px] rounded-xl overflow-hidden"
                style={{ padding: 2, background: c.active ? ARENA.goldFrame : 'var(--stone-200)' }}>
                <div className="rounded-lg overflow-hidden bg-stone-200" style={{ aspectRatio: '9/12' }}>
                  <img src={c.thumb || c.image} alt="" className="w-full h-full object-cover" />
                </div>
                <p className="text-[8px] font-display font-bold truncate px-1 py-0.5">{c.name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="label">Available chariots</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {data?.variants?.map(v => {
          const isOwned = ownedIds.has(v.id);
          return (
            <button key={v.id} onClick={() => !isOwned && setSel(v.id)} disabled={isOwned}
              className={`relative rounded-xl overflow-hidden text-left transition
                ${isOwned ? 'opacity-40' : sel === v.id ? 'scale-[1.03]' : 'hover:scale-[1.01]'}`}
              style={{
                padding: 2,
                background: sel === v.id ? ARENA.goldFrame : 'var(--stone-200)',
                boxShadow: sel === v.id ? t.glowCss : 'none',
              }}>
              <div className="rounded-lg overflow-hidden bg-stone-200" style={{ aspectRatio: '9/13' }}>
                <img src={v.thumb || v.image} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-1.5"
                style={{ background: 'linear-gradient(180deg,transparent,rgba(36,30,22,.9))' }}>
                <p className="text-[10px] font-display font-black text-white truncate">{v.name}</p>
                <p className="text-[8px] text-white/70">{v.modelType}</p>
              </div>
              {isOwned && <span className="absolute top-1.5 left-1.5 chip-mint !text-[7px]">OWNED</span>}
            </button>
          );
        })}
      </div>

      {sel && (
        <>
          <div className="mt-3">
            <label className="label">Name (optional)</label>
            <input className="input" value={name} maxLength={40}
              placeholder={data?.variants?.find(v => v.id === sel)?.name}
              onChange={e => setName(e.target.value)} />
          </div>
          <button onClick={() => buy.mutate()} disabled={buy.isPending}
            className="btn-primary w-full mt-3 py-3">
            {buy.isPending ? <><Loader2 size={16} className="animate-spin" /> Claiming…</>
              : owned.length === 0 ? <><Sparkles size={16} /> Claim — FREE</>
              : <>Buy · 💎{compact(4000)}</>}
          </button>
        </>
      )}
    </Sheet>
  );
}